import List from '@mui/material/List';
import Collapse from '@mui/material/Collapse';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ArticleIcon from '@mui/icons-material/Article';
import HistoryIcon from '@mui/icons-material/History';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import Tooltip from '@mui/material/Tooltip';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { routeAccessConfig } from '~/shared/config/access';
import { usersApi } from '~/features/user/model/api/users-api';
import { NavLink, useLocation } from 'react-router-dom';

export default function MenuContent({ collapsed = false }: { collapsed?: boolean }) {

  const { data } = useSelector(usersApi.endpoints.getUserInfo.select(undefined)) as any;
  const roles = new Set((data?.roles ?? []).map((r: string) => r.toLowerCase()));
  const isAdmin = roles.has('admin');
  const location = useLocation();
  const [isDictionaryOpen, setIsDictionaryOpen] = useState(false);

  useEffect(() => {
    if (location.pathname.startsWith('/dictionaries')) {
      setIsDictionaryOpen(true);
    }
  }, [location.pathname]);

  const menuConfig = [
    { text: 'Аккаунты', icon: <PeopleRoundedIcon />, link: '/accounts' },
    { text: 'Шаблоны', icon: <ArticleIcon />, link: '/templates' },
    { text: 'История создания кампаний', icon: <HistoryIcon />, link: '/launch-history' },
    { text: 'Пользователи', icon: <PersonOutlineIcon />, link: '/users' },
  ].map(item => ({
    ...item,
    roles: routeAccessConfig.find(r => r.path === item.link)?.roles ?? [],
  }));

  const dictionaryItems = [
    { text: 'CTA', icon: <ArticleIcon />, link: '/dictionaries/cta' },
  ].map(item => ({
    ...item,
    roles: routeAccessConfig.find(r => r.path === item.link)?.roles ?? [],
  }));

  const mainListItems = useMemo(
    () => menuConfig.filter(item => isAdmin || item.roles.some(r => roles.has(r))),
    [roles, isAdmin]
  );

  const visibleDictionaryItems = useMemo(
    () => dictionaryItems.filter(item => isAdmin || item.roles.some(r => roles.has(r))),
    [roles, isAdmin]
  );

  const renderNavItem = (
    item: { text: string; icon: JSX.Element; link: string },
    isChild = false,
  ) => (
    <ListItem key={item.link} disablePadding sx={{ display: 'block' }}>
      <Tooltip title={collapsed ? item.text : ''} placement="right">
        <ListItemButton
          component={NavLink}
          to={item.link}
          sx={{
            textDecoration: 'none',
            borderRadius: 1,
            justifyContent: collapsed ? 'center' : 'flex-start',
            px: collapsed ? 1 : isChild ? 3 : 2,
            py: 1,
            minHeight: 14,
            '&:hover': {
              textDecoration: 'none',
            },
            '&::before': {
              display: 'none',
            },
            '&.active': {
              backgroundColor: 'action.selected',
            },
            '&.active .MuiListItemIcon-root': {
              color: 'primary.main',
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: collapsed ? 'unset' : 0,
              mr: collapsed ? 0 : 1,
              justifyContent: 'center',
              '& svg': { fontSize: 18 },
            }}
          >
            {item.icon}
          </ListItemIcon>
          {!collapsed && (
            <Box component="span" sx={{ fontSize: 15, fontWeight: 100 }}>
              {item.text}
            </Box>
          )}
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );

  return (
    <Stack sx={{ flexGrow: 1, p: 1, justifyContent: 'space-between' }}>
      <List dense sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {mainListItems.map(item => renderNavItem(item))}
        {visibleDictionaryItems.length > 0 && (
          <>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <Tooltip title={collapsed ? 'Справочник' : ''} placement="right">
                <ListItemButton
                  onClick={() => setIsDictionaryOpen(prev => !prev)}
                  sx={{
                    borderRadius: 1,
                    justifyContent: collapsed ? 'center' : 'space-between',
                    px: collapsed ? 1 : 2,
                    py: 1,
                    minHeight: 14,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={collapsed ? 0 : 1}>
                    <ListItemIcon
                      sx={{
                        minWidth: collapsed ? 'unset' : 0,
                        mr: collapsed ? 0 : 0,
                        justifyContent: 'center',
                        '& svg': { fontSize: 18 },
                      }}
                    >
                      <MenuBookRoundedIcon />
                    </ListItemIcon>
                    {!collapsed && (
                      <Box component="span" sx={{ fontSize: 15, fontWeight: 100 }}>
                        Справочник
                      </Box>
                    )}
                  </Stack>
                  {!collapsed && (
                    isDictionaryOpen ? <KeyboardArrowDownRoundedIcon /> : <KeyboardArrowRightRoundedIcon />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            <Collapse in={collapsed ? true : isDictionaryOpen} timeout="auto" unmountOnExit={!collapsed}>
              <List dense disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {visibleDictionaryItems.map(item => renderNavItem(item, true))}
              </List>
            </Collapse>
          </>
        )}
      </List>
    </Stack >
  );
}
